// tslint:disable:no-bitwise

import { 
  Component,
  OnInit,
  Renderer2,
  ElementRef,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { MarkerModel } from 'src/app/story/models/marker.model';

import { filter, takeUntil, map } from 'rxjs/operators';
import { EngineState } from 'src/app/engine/state/engine.state';
import { EngineService, EngineEventsEnum } from 'src/app/engine/engine.service';
import { SelectLabel, SetLabel } from 'src/app/story/state/labels/story.labels.actions';
import { EnginePositionModel } from 'src/app/engine/models/engine-position.model';
import { Observable, Subject, Subscription } from 'rxjs';

import * as Hammer from 'hammerjs';
import { HitModel } from 'src/app/engine/models/hit.model';
import { CameraService } from 'src/app/engine/camera.service';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { FlyToNode } from 'src/app/engine/state/engine.actions';
import { SetCamera, SetZoomed } from 'src/app/engine/state/camera/engine.camera.actions';
import { EngineCameraState } from 'src/app/engine/state/camera/engine.camera.state';
import { LabelPinType } from 'src/app/story/models/labels/label-pin-type.enum';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { ErrandType } from 'src/app/story/models/errands/errand-type.enum';

import * as _ from 'lodash';
import { ErrandInterface } from 'src/app/story/models/errands/errand.model';

declare const Human: any;

const fullOpacity = 1; 
const fadeStartAngle = 80;
const maxViewAngle = 100;
const fadeRange = maxViewAngle - fadeStartAngle;

// Hardcoded reference number. The actual pin width is set in label.component.scss.
const pinDiameter = 32;
const pinRadius = pinDiameter / 2;

enum LabelStyleClass {
  fadeIn = 'label--fade-in',
  fadeOut = 'label--fade-out',
  tagLeft = 'label__tag--left',
  tagRight = 'label__tag--right'
}

@Component({
  selector: 'app-label',
  templateUrl: './label.component.html',
  styleUrls: ['./label.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabelComponent implements OnInit, AfterViewInit, OnDestroy {
  labelPinType = LabelPinType;

  @ViewChild('labelContainerEl') labelContainerRef: ElementRef;
  @ViewChild('labelEl') labelRef: ElementRef;
  @ViewChild('wire') wireRef: ElementRef;
  @ViewChild('tag') tagRef: ElementRef;
  @ViewChild('pin') pinRef: ElementRef;
  @ViewChild('title') titleRef: ElementRef;

  @Select(EngineState.getEnginePosition)
  enginePosition$: Observable<EnginePositionModel>;

  @Select(StoryLabelsState.getSelectedLabel)
  selectedLabel$: Observable<LabelModel>;

  @Input()
  public get label(): LabelModel {
    return this._label;
  }
  public set label(value: LabelModel) {
    const isValueNull = Object.keys(value).length === 0;
    const isLabelNull = this.label == null || Object.keys(this.label).length === 0;
    
    if (isValueNull && !isLabelNull) {
      this._label = value;
      this.destroyLabel();
    } else if (!isValueNull) {
      this._label = value;
      this.initLabel(this.label);
    }

  }
  private _label: LabelModel;

  @Input()
  theme = 'classic';

  // Cache position for performance
  enginePosition: EnginePositionModel = null; 

  marker: MarkerModel;

  // Position updater
  positionUpdate: { id: string, canvasPos: any };

  // Label visibility
  depth: Number;
  inView: boolean;

  // Local expanded / collapsed references
  descriptionCollapsed = false;
  tagCollapsed = false;

  // Pin
  pinDragging = false;

  // Wire
  hasWire: boolean;

  dragLabelStartEvent: HammerInput;
  cachedTagOffset: [number, number];

  // Reference to hammer event managers so we can unbind on destroy
  hammerManagers: HammerManager[] = [];

  object$: Subscription;

  private ngUnsubscribe = new Subject();

  constructor(
    private renderer: Renderer2,
    private engineEventsService: EngineService,
    private cameraService: CameraService,
    private store: Store,
    private changeDetection: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initListeners();
  }

  ngAfterViewInit() {
    // We're using HammerJS to handle drag events for better touch compatibility. However
    // this results in Hammer 'tap' events preceeding Angular 'click' events, which can
    // lead to duplicate or unexpected click handling. So for labels, all interaction events are 
    // now handled through HammerJS, and initialized below. 
    this.bindTagEvents();
    this.bindPinEvents();
  }

  ngOnDestroy() {
    this.destroyLabel();

    // Unsubscribe
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  destroyLabel() {
    // Destroy event handlers bound outside of angular
    this.hammerManagers.forEach(manager => manager.destroy);
    this.hammerManagers = [];

    // Delete markers in the engine
    this.deleteMarker(this.marker);
  }

  initListeners() {
    // Handle selected label
    this.selectedLabel$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(label => this.handleLabelSelected(label));

    // Cache local copy of engine position any time it updates, for performance
    this.enginePosition$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((position) => {
        this.enginePosition = position;
      });

    // An exception of allowing another component to subscribe directly to engine events. 
    // Since marker position changes with any motion from the engine, running events through 
    // the store would create an unneccessary performance overhead. 
    this.engineEventsService.getEvent(EngineEventsEnum.SceneMarkersUpdated)
      .pipe(
        filter(() => {
          return this.label != null && this.enginePosition != null;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.setCanvasPos();
      });
  }

  bindTagEvents() {
    // Create a HammerJS manager so we can custom define single and double click
    const mc = new Hammer(this.tagRef.nativeElement, { domEvents: true });
    mc.get('pan').set({ direction: Hammer.DIRECTION_ALL, threshold: 3 });
    
    mc.on('pan', (event) => {
      event.srcEvent.stopPropagation();

      if (!this.cachedTagOffset) {
        this.cachedTagOffset = this.label.offset;
      }
      
      const offset: [number, number] = [
        this.cachedTagOffset[0] + event.deltaX,
        this.cachedTagOffset[1] + event.deltaY
      ];

      this.label.offset = offset;

      if (event.isFinal) {
        this.cachedTagOffset = null;

        this.store.dispatch(new SetLabel(this.label));
      }
    });

    mc.on('tap', (event) => {
      event.srcEvent.preventDefault();
      event.srcEvent.stopPropagation();

      // Hack to make expand button work differently from rest of tag.
      // Normally wouldn't have to do this, but HammerJS creates very strange
      // event handling loops. 
      if (event.target.parentNode.nodeName === 'BUTTON') {
        this.toggleDescription();
        // this.changeDetection.markForCheck();
      } else {
        this.selectLabel();
      }
    });

    this.hammerManagers.push(mc);
  }

  bindPinEvents() {
    // Create a HammerJS manager so we can custom define single and double click
    const mc = new Hammer(this.pinRef.nativeElement);
    mc.get('pan').set({ direction: Hammer.DIRECTION_ALL, threshold: 0 });
    
    mc.on('pan', (event) => {
      event.srcEvent.stopPropagation();
      
      // While pinDragging is true, setCanvasPos won't fire, which allows us to 
      // change the pin X and Y translation without interference from the engine.
      this.pinDragging = true;

      let { layerX, layerY} = event.changedPointers[0];

      layerX = layerX - pinRadius;
      layerY = layerY - pinRadius;

      // Marker is updated by the engine every tick, so we have to kill it or else the engine
      // will overwrite our changes. 
      if (this.marker) {
        this.deleteMarker(this.marker);
      }
      
      // Manually update the pin position to the mouse cursor position
      this.renderer.setStyle(this.labelContainerRef.nativeElement, 'transform', `translate(${layerX}px, ${layerY}px)`);

      if (event.isFinal) {
        this.pinDragging = false;
        const label = {...this.label};

        // If the pin is dropped on a node, update the position. Otherwise just revert the pin back to 
        // it's original location.
        const hit = Human.renderer.pick.pick({ canvasPos: [layerX, layerY], rayPick: true }) as HitModel;
        if (hit.objectId) {
          label.dir = this.cameraService.getDirection(hit);
          label.objectId = hit.objectId;
          label.barycentric = [hit.barycentric[0], hit.barycentric[1], hit.barycentric[2]];
          label.pos = [hit.worldPos[0], hit.worldPos[1], hit.worldPos[2]];
          label.primitiveIndex = hit.primitiveIndex;
        }
        this.store.dispatch(new SetLabel(label));
      }
    });

    mc.on('tap', (event) => {
      event.srcEvent.preventDefault();
      event.srcEvent.stopImmediatePropagation();

      this.selectLabel();
    });

    this.hammerManagers.push(mc);
  }

  initLabel(label: LabelModel) {
    this.initMarker(label);
    this.initObjectSubscription(label);
    this.ensureObjectId(label);

    this.descriptionCollapsed = label.collapseDescription;
    this.tagCollapsed = label.hideTag;
  }
  
  initMarker(label: LabelModel) {
    if (this.marker) {
      this.deleteMarker(this.marker);
    }

    this.marker = Human.scene.markers.create({
      pos: label.pos,
      dir: label.dir,
      objectId: label.objectId,
      primitiveIndex: label.primitiveIndex,
      barycentric: label.barycentric,
      renderableIndex: label.renderableIndex || 0
    });
  }

  // labels should always have an objectId
  ensureObjectId(label: LabelModel) {
    if (!label.objectId) {
      const hit = Human.renderer.pick.pick({ canvasPos: [this.marker.canvasPos[0], this.marker.canvasPos[1]], rayPick: true }) as HitModel;
      label.objectId = hit.objectId;
      label.states.forEach((labelState) => {
        labelState.errands.forEach((errand) => {
          if (errand.type === ErrandType.flyToObject) {
            errand.value = hit.objectId;
          }
        });
      });
      this.store.dispatch(new SetLabel(label));
    }
  }

  initObjectSubscription(label) {
    if (this.object$) {
      this.object$.unsubscribe();
    }

    // Listen to attached node and check for visibility Don't show labels with
    // hidden nodes 
    // #NOTE: [EM] This information should be available by the
    // marker and is more reliable, since view-graph node does not necessarily
    // map 1-to-1 for every scene graph object
    if (label.objectId) {
      this.object$ = this.store.select(EngineState.getViewGraphNode)
        .pipe(
          map(filterFn => filterFn(label.objectId)),
          filter(node => !!node),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(node => {
          this.renderer.setStyle(this.labelContainerRef.nativeElement, 'display', node.boolState.shown ? 'block' : 'none');
        });
    }
  }

  deleteMarker(marker: MarkerModel) {
    Human.scene.markers.delete(marker.id);
    this.marker = null;
  }

  toggleDescription(open?: boolean) {
    open = (typeof open === 'undefined') ? !this.descriptionCollapsed : open;
    this.descriptionCollapsed = open;
  }

  handleLabelSelected(label: LabelModel) {
    const isSelected = (label && label.id === this.label.id);
    const errands = <ErrandInterface[]>_.get(label, 'states[1].errands');
    const cameraLocked = this.store.selectSnapshot(EngineCameraState.getLockCamera);

    // Fly to
    if (isSelected && errands && !cameraLocked) {
      errands.forEach(errand => {
        switch (errand.type) {
          case ErrandType.flyToObject:
            this.flyToObject(errand.value);
            break;

          case ErrandType.flyToCamera:
            this.flyToPosition(errand.value);
            break;
        }

        this.store.dispatch(new SetZoomed(true));
      });
      
    }

    // Expand
    if (this.label.expandTag && this.label.hideTag) {
      this.tagCollapsed = !isSelected;
    } else {
      this.tagCollapsed = this.label.hideTag;
    }

    // Show description
    if (this.label.showDescription && this.label.collapseDescription) {
      this.descriptionCollapsed = !isSelected;
    } else {
      this.descriptionCollapsed = this.label.collapseDescription;
    }
  }

  flyToObject(objectId) {
    const graph = this.store.selectSnapshot(EngineState.getViewGraph);
    const node = this.engineEventsService.getNode(graph, objectId);

    this.store.dispatch(new FlyToNode(node));
  }

  flyToPosition(cameraPosition) {
    this.store.dispatch(new SetCamera(cameraPosition));
  }

  setCanvasPos() {
    if (this.pinDragging || !this.marker) {
      return;
    }

    this.depth = -this.marker.viewPos[2];

    // The position can update the display state via the inView property
    // Only call if inView has changed
    const inView = !this.isOccluded();

    if (inView !== this.inView) {
      this.inView = inView;
      this.setDisplay(inView);
    }

    // this.setOpacity();
    this.setElementPosition();
    this.setTagPosition();
    this.setWirePosition();
    
    // Finally, notify
    this.positionUpdate = {
      id: this.label.id,
      canvasPos: this.marker.canvasPos
    };

    // This is fired for the API's consumption
    // We have to use Human.events in order to hook into its event system
    // this.engineEventsService.fireEvent(EngineEventsEnum.HotspotsPositionUpdated, this.positionUpdate);
  }

  selectLabel() {
    const previousLabel = this.store.selectSnapshot(StoryLabelsState.getSelectedLabel);
    // Clicking same label to unselect
    if (previousLabel && previousLabel.id === this.label.id) {
      this.store.dispatch(new SelectLabel(null));
    } else {
      this.store.dispatch(new SelectLabel(this.label));
    }
  }

  isOccluded() {
    return (
      this.label.occludable && 
      (
        Math.abs(this.marker.viewAngle) > maxViewAngle ||
        this.marker.occluded ||
        this.depth < 0
      )
    );
  }

  /**
   * Show and hide labels
   */
  setDisplay(transition: boolean) {
    const labelEl = this.labelRef.nativeElement;

    // Remove any previous transition classes
    this.renderer.removeClass(labelEl, LabelStyleClass.fadeIn);
    this.renderer.removeClass(labelEl, LabelStyleClass.fadeOut);

    if (transition) {
      const cssClass = (this.inView) ? LabelStyleClass.fadeIn : LabelStyleClass.fadeOut;
      this.renderer.addClass(labelEl, cssClass);
      this.renderer.setStyle(labelEl, 'visibility', 'visible');
    } else {
      const display = (this.inView) ? 'visible' : 'hidden';
      this.renderer.setStyle(labelEl, 'visibility', display);
    }
    
    // This is fired for the API's consumption
    // We have to use Human.events in order to hook into its event system
    const displayUpdateEvent = {
      id: this.label.id,
      inView: this.inView,
      shown: this.inView
    };

    Human.events.fire('hotspots.displayUpdated', displayUpdateEvent);
  }

  setOpacity() {
    let opacity = fullOpacity;

    if (this.marker.viewAngle >= fadeStartAngle) {
      const angleDiff = this.marker.viewAngle - fadeStartAngle;
      const scaledOpacity = (angleDiff / fadeRange) * fullOpacity;

      opacity = Math.max(1 - scaledOpacity, 0);
    }
    
    this.renderer.setStyle(this.labelContainerRef.nativeElement, 'opacity', opacity);
  }

  setElementPosition() {
    const markerPos = this.marker.canvasPos;
    this.renderer.setStyle(this.labelContainerRef.nativeElement, 'transform', `translate(${markerPos[0]}px, ${markerPos[1]}px)`);
  }

  setTagPosition() {
    const offset = this.label.offset;
    const tagWidth = this.tagRef.nativeElement.offsetWidth;
    const side = (this.label.offset[0] > 0) ? 'left' : 'right';

    switch (side) {
      case 'left':
        this.renderer.setStyle(this.tagRef.nativeElement, 'transform', `translate(${16 + offset[0]}px, ${offset[1]}px)`);
        break;
      
      case 'right':
        this.renderer.setStyle(this.tagRef.nativeElement, 'transform', `translate(${16 + offset[0] - tagWidth}px, ${offset[1]}px)`);
        break;
    }
  }

  setWirePosition() {
    // tslint:disable-next-line:prefer-const
    let [x, y] = this.label.offset;

    // For annotation and hotspot, move line down to below title
    switch (this.label.theme) {
      case LabelThemeEnum.Annotation:
      case LabelThemeEnum.Classic: 
        const height = this.titleRef.nativeElement.offsetHeight - 16 - 2; // 16px is half of pin, 2 is border
        y += height;
        break;
    }
    
    const wireLength = Math.sqrt(x * x + y * y);

    this.renderer.setStyle(this.wireRef.nativeElement, 'width', `${wireLength}px`);
    this.renderer.setStyle(this.wireRef.nativeElement, 'transform', `rotate(${Math.atan2(y, x)}rad)`);
  }
}
